import ChartNode from '../../../assets/interfaces/ChartNode';
import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { now } from 'lodash';
import TkOrganogramConfig from '../../../assets/interfaces/TkOrganogramConfig';
import OrganizationChart from 'vue-organization-chart';

@Component({
  name: 'TkOrganogram',
  components: {
    OrganizationChart,
  },
})
class TkOrganogram extends Vue {
  // #region [ PROPS ]
  @Prop({ default: () => {} })
  private config: TkOrganogramConfig;

  @Prop({ required: true })
  private readonly datasource: ChartNode;
  // #endregion

  // #region [ STORE ]
  // #endregion

  // #region [ EVENTS ]
  @Emit('node-click')
  private emitNodeClick(node: ChartNode) {}

  @Emit('single-click')
  private emitSingleClick(node: ChartNode) {}

  @Emit('double-click')
  private emitDoubleClick(node: ChartNode) {}
  // #endregion

  // #region [ DATA ]
  private static readonly DEFAULT_CONFIG: TkOrganogramConfig = {
    doubleClickTime: 300,
    pan: true,
    zoom: true,
    doubleClickFocus: false,
  };

  private internalConfig = self.DEFAULT_CONFIG;

  public focusedNode: ChartNode = null;

  private lastClickedNodeId: number | string = null
  private timeLastNodeWasClicked: number = null;

  private singleClickTimeout: NodeJS.Timeout = null;
  // #endregion

  // #region [ COMPUTED ]
  // #endregion

  // #region [ WATCHERS ]
  @Watch('config', { immediate: true, deep: false })
  private configOnChange(): void {
    this.internalConfig = {
      ...self.DEFAULT_CONFIG,
      ...this.config,
    };
  }

  @Watch('datasource', { immediate: true, deep: false })
  private datasourceOnChange(): void {
    this.focusNode(this.datasource);
  }
  // #endregion

  // #region [ LIFECYCLE ]
  // #endregion

  // #region [ METHODS ]
  public onNodeClick(node: ChartNode): void {
    this.emitNodeClick(node);
    this.cancelSingleClickTimeout();

    if (this.verifyDoubleClick(node)) {
      this.onDoubleClick(node);
    } else {
      this.startSingleClickTimeout(node);
    }

    this.lastClickedNodeId = node.id;
    this.timeLastNodeWasClicked = now();
  }

  /** Inicia um timeout que executará a função de clique simples após alguns milisegundos. */
  private startSingleClickTimeout(node: ChartNode): void {
    this.singleClickTimeout = setTimeout(() => {
      this.singleClickTimeout = null;
      this.onSingleClick(node);
    }, this.internalConfig.doubleClickTime);
  }

  /** Cancela o timeout previamente iniciado, evitando a execução da função de clique simples. */
  private cancelSingleClickTimeout(): void {
    if (this.singleClickTimeout) {
      clearTimeout(this.singleClickTimeout);
      this.singleClickTimeout = null;
    }
  }

  /** Função a ser executada após a detecção de clique simples em um nódulo. */
  private onSingleClick(node: ChartNode): void {
    this.emitSingleClick(node);
  }

  /** Função a ser executada após a detecção de clique suplo em um nódulo. */
  private onDoubleClick(node: ChartNode): void {
    if (this.internalConfig.doubleClickFocus) {
      this.focusNode(node);
    }

    this.emitDoubleClick(node);
  }

  /** Mostra todo o organograma abaixo do nódulo raiz. */
  private focusRootNode(): void {
    this.focusedNode = this.datasource;
  }

  /** Mostra todos o nódulo e todos seus inferiores. */
  private focusNode(node: ChartNode): void {
    this.focusedNode = node;
  }

  private timeSinceLastNodeWasClicked(): number {
    const currentTime = now();
    const elapsedTime = currentTime - this.timeLastNodeWasClicked;

    return elapsedTime;
  }

  private verifyDoubleClick(node: ChartNode): boolean {
    if (this.lastClickedNodeId !== node.id) {
      return false;
    }

    const isDoubleClick = this.timeSinceLastNodeWasClicked() < this.internalConfig.doubleClickTime;

    return isDoubleClick;
  }

  public resetChart(): void {
    this.focusedNode = null;
    this.lastClickedNodeId = null;
    this.timeLastNodeWasClicked = null;
  }
  // #endregion
}

const self = TkOrganogram;

export default TkOrganogram;
